import React, { useCallback, useMemo, useState } from "react";

import { orderBy } from "lodash";
import { useParams } from "react-router-dom";

import useAllAccounts from "@fartherfinance/frontend/api/Accounts/hooks/useAllAccounts";
import { PlatformAccountType } from "@fartherfinance/frontend/api/FinancialPlans/account/types";
import {
  GoalBasedFinancialPlanId,
  GoalBasedFinancialPlanSavingsId,
} from "@fartherfinance/frontend/api/FinancialPlans/ids";
import useGetGoalBasedFinancialPlan from "@fartherfinance/frontend/api/FinancialPlans/plan/hooks/useGetGoalBasedFinancialPlan";
import useDeleteGoalBasedFinancialPlanSavings from "@fartherfinance/frontend/api/FinancialPlans/savings/hooks/useDeleteGoalBasedFinancialPlanSavings";
import usePatchGoalBasedFinancialPlanSavings from "@fartherfinance/frontend/api/FinancialPlans/savings/hooks/usePatchGoalBasedFinancialPlanSavings";
import usePostGoalBasedFinancialPlanSavings from "@fartherfinance/frontend/api/FinancialPlans/savings/hooks/usePostGoalBasedFinancialPlanSavings";
import {
  AltAssetId,
  ClientId,
  ExternalAccountId,
  FartherAccountId,
} from "@fartherfinance/frontend/api/Types";

import CardsGrid from "../Shared/CardsGrid/CardsGrid";
import { defaultPlanDateFieldValues, OwnerField } from "../Shared/constants";
import EmptyCard from "../Shared/EmptyCard/EmptyCard";
import FilledCard from "../Shared/FilledCard/FilledCard";
import { getInitialPlanDateFieldValuesForForm } from "../Shared/helpers/getInitialPlanDateFieldValuesForForm";
import { ClientAndSpouseDetails } from "../Shared/hooks/useGetClientAndSpouseDetails";
import useRequestAuth from "@src/multiCustodian/hooks/useRequestAuth";
import BodyContentFormatter from "@src/sharedComponents/BodyContentFormatter/BodyContentFormatter";
import CustomDropdownBase from "@src/sharedComponents/CustomDropdownBase/CustomDropdownBase";
import PageHero from "@src/sharedComponents/PageHero/PageHero";
import TitleBlock from "@src/sharedComponents/PageHero/TitleBlock";
import Text from "@src/sharedComponents/Typography/Text";
import { formatExternalAccountName } from "@src/utils/ExternalAccount/formatExternalAccountName";
import { getExternalAccountName } from "@src/utils/ExternalAccount/getExternalAccountName";
import { pluralize } from "@src/utils/pluralize";
import toUSD from "@src/utils/toUSD";

import SavingsFormDrawer from "./Form";
import { AddedAccountOptionExtended } from "./Types";

import styles from "./Savings.module.css";

const getPlatformTypeRank = (
  a: PlatformAccountType | undefined,
  b: PlatformAccountType | undefined
) => {
  if (a === undefined) {
    return 1;
  }

  if (a == b) {
    return 0;
  }

  if (a === "FartherManagedAccount") {
    return -1;
  }

  if (a === "ManuallyTrackedAccount" && b === "ExternalAccount") {
    return -1;
  }

  if (a === "ExternalAccount") {
    return 1;
  }

  return 0;
};

interface Props {
  clientSpouseData: ClientAndSpouseDetails;
}

const FinancialPlanSavingsAccounts: React.FC<Props> = ({
  clientSpouseData,
}): JSX.Element => {
  const { clientId, goalBasedFinancialPlanId } = useParams<{
    clientId: ClientId;
    goalBasedFinancialPlanId: GoalBasedFinancialPlanId;
  }>();

  const [goalBasedFinancialPlanSavingsId, setGoalBasedFinancialPlanSavingsId] =
    useState<GoalBasedFinancialPlanSavingsId | undefined>(undefined);

  const [showDrawer, setShowDrawer] = useState<boolean>(false);

  const [defaultAccount, setDefaultAccount] = useState<
    AddedAccountOptionExtended | undefined
  >(undefined);

  const auth = useRequestAuth();

  const financialPlan = useGetGoalBasedFinancialPlan(
    goalBasedFinancialPlanId,
    auth
  );
  const allAccounts = useAllAccounts(clientId, auth);

  const { client, spouse } = clientSpouseData;

  const { fullName: clientFullName } = client;

  const spouseFullName = spouse?.fullName ?? "Spouse";

  const getNameOfClientOrSpouse = useCallback(
    (ownerId: ClientId) => {
      return ownerId === clientId ? clientFullName : spouseFullName;
    },
    [clientId, clientFullName, spouseFullName]
  );

  const ownersOptions = useMemo(() => {
    if (financialPlan.data?.retirementHorizon === undefined) {
      return [];
    }

    return financialPlan.data.retirementHorizon.map((rh) => ({
      label: rh.owner === clientId ? clientFullName : spouseFullName,
      value: rh.owner,
    }));
  }, [
    clientId,
    clientFullName,
    spouseFullName,
    financialPlan.data?.retirementHorizon,
  ]);

  const {
    mutateAsync: deleteGoalBasedFinancialPlanSavings,
    isLoading: isMutatingDelete,
  } = useDeleteGoalBasedFinancialPlanSavings(auth);

  const {
    mutateAsync: patchGoalBasedFinancialPlanSavings,
    isLoading: isMutatingPatch,
  } = usePatchGoalBasedFinancialPlanSavings(auth);

  const {
    mutateAsync: postGoalBasedFinancialPlanSavings,
    isLoading: isMutatingPost,
  } = usePostGoalBasedFinancialPlanSavings(auth);

  const mutations = useMemo(
    () => ({
      onAddSavings: postGoalBasedFinancialPlanSavings,
      onUpdateSavings: patchGoalBasedFinancialPlanSavings,
      onDeleteSavings: deleteGoalBasedFinancialPlanSavings,
      isMutating: isMutatingDelete || isMutatingPatch || isMutatingPost,
    }),
    [
      postGoalBasedFinancialPlanSavings,
      patchGoalBasedFinancialPlanSavings,
      deleteGoalBasedFinancialPlanSavings,
      isMutatingDelete,
      isMutatingPatch,
      isMutatingPost,
    ]
  );

  const existingSavingsElement = useMemo(() => {
    if (goalBasedFinancialPlanSavingsId === undefined) {
      return undefined;
    }

    return (financialPlan.data?.savings ?? []).find(
      (s) => s.savingsId === goalBasedFinancialPlanSavingsId
    );
  }, [financialPlan.data, goalBasedFinancialPlanSavingsId]);

  const addedAccountOptions: AddedAccountOptionExtended[] = useMemo(() => {
    if (financialPlan.isLoading || financialPlan.hasError) {
      return [];
    }

    if (allAccounts.isLoading || allAccounts.hasError) {
      return [];
    }

    return (financialPlan.data?.accounts ?? [])
      .filter((el) => {
        if (el.accountType === "ManuallyTrackedAccount") {
          const mta = (allAccounts.data.manuallyTrackedAccounts ?? []).find(
            (a) => el.accountId === a.id
          );

          if (mta === undefined) {
            throw new Error(
              `Manually tracked account: ${el.accountId} not found in list of accounts`
            );
          }

          if (
            mta.assetType === "Collectable" ||
            mta.assetType === "Real Estate"
          ) {
            return false;
          }

          return true;
        } else if (el.accountType === "ExternalAccount") {
          const exAcct = (allAccounts.data.externalAccounts ?? []).find(
            (a) => el.accountId === a.accountId
          );

          if (exAcct === undefined) {
            throw new Error(
              `External account: ${el.accountId} not found in list of accounts`
            );
          }

          if (
            exAcct.details.account.accountType !== "savings" &&
            exAcct.details.account.accountType !== "checking" &&
            exAcct.details.account.accountType !== "depository" &&
            exAcct.details.account.accountType !== "investment"
          ) {
            return false;
          }
        }

        return true;
      })
      .map((acc) => {
        let label = undefined;
        let accountType = undefined;
        let ownerId = undefined;
        if (acc.accountType === "FartherManagedAccount") {
          const account = (allAccounts.data.fartherAccounts ?? []).find(
            (el) => el.virtualAccountId === acc.accountId
          );
          if (account === undefined) {
            throw new Error(`Managed account: ${acc.accountId} not found`);
          }

          label = account.accountDetails.displayName;
          accountType = account.accountDetails.accountType;
          ownerId = account.accountOwner[0];
        } else if (acc.accountType === "ManuallyTrackedAccount") {
          const account = (allAccounts.data.manuallyTrackedAccounts ?? []).find(
            (el) => el.id === acc.accountId
          );
          if (account === undefined) {
            throw new Error(`Manual account: ${acc.accountId} not found`);
          }

          label = account.assetName;
          accountType = account.assetType;
          ownerId = account.personId;
        } else {
          const account = (allAccounts.data.externalAccounts ?? []).find(
            (el) => el.accountId === acc.accountId
          );
          if (account === undefined) {
            throw new Error(`External account: ${acc.accountId} not found`);
          }

          label = formatExternalAccountName(
            getExternalAccountName(account),
            14
          );
          accountType = account.details.account.accountType;
          ownerId = account.clientId;
        }

        return {
          label,
          value: acc.accountId,
          accountType,
          ownerId,
          platformType: acc.accountType,
        };
      });
  }, [financialPlan, allAccounts]);

  const getCardHeading = useCallback(
    (accountId: FartherAccountId | AltAssetId | ExternalAccountId) => {
      const accountType =
        addedAccountOptions.find((a) => a.value === accountId)?.accountType ??
        "N/A";

      return `${accountType[0].toUpperCase()}${accountType.substring(1)}`;
    },
    [addedAccountOptions]
  );

  const getCardHeadingRight = useCallback(
    (accountId: FartherAccountId | AltAssetId | ExternalAccountId) => {
      return (
        addedAccountOptions.find((a) => a.value === accountId)?.label ?? "N/A"
      );
    },
    [addedAccountOptions]
  );

  const getPlatformType = useCallback(
    (accountId: FartherAccountId | AltAssetId | ExternalAccountId) => {
      return addedAccountOptions.find((a) => a.value === accountId)
        ?.platformType;
    },
    [addedAccountOptions]
  );

  const initialPlanDateAndOwnerFields = useMemo(() => {
    let ownerField = { label: clientFullName, value: clientId };

    if (existingSavingsElement === undefined) {
      return {
        [OwnerField]: ownerField,
        ...defaultPlanDateFieldValues,
      };
    }

    if (financialPlan.data === undefined) {
      return undefined;
    }

    if (
      existingSavingsElement?.owner !== undefined &&
      existingSavingsElement.owner !== clientId
    ) {
      ownerField = {
        label: spouseFullName,
        value: existingSavingsElement.owner,
      };
    }

    return {
      ...getInitialPlanDateFieldValuesForForm({
        clientId,
        retirementHorizons: financialPlan.data.retirementHorizon,
        existingCategoryElement: existingSavingsElement,
      }),
      [OwnerField]: ownerField,
    };
  }, [
    clientId,
    financialPlan,
    existingSavingsElement,
    clientFullName,
    spouseFullName,
  ]);

  const onOpenDrawer = () => {
    setDefaultAccount(undefined);
    setGoalBasedFinancialPlanSavingsId(undefined);
    setShowDrawer(true);
  };

  const onCloseDrawer = () => {
    setShowDrawer(false);
  };

  const onClickCard = useCallback(
    (
      savingsId: GoalBasedFinancialPlanSavingsId | undefined,
      defaultAccount: AddedAccountOptionExtended | undefined
    ) => {
      setDefaultAccount(defaultAccount);
      setGoalBasedFinancialPlanSavingsId(savingsId);
      setShowDrawer(true);
    },
    []
  );

  const spouseInPlan = useMemo(() => {
    return (financialPlan.data?.retirementHorizon ?? []).some(
      (r) => r.owner !== clientId
    );
  }, [clientId, financialPlan.data]);

  const sortedSavings = useMemo(() => {
    if (financialPlan.data === undefined) {
      return [];
    }

    const firstSort = orderBy(
      financialPlan.data.savings,
      [
        (el) => getCardHeading(el.account),
        (el) => getCardHeadingRight(el.account),
      ],
      ["asc", "asc"]
    );

    return firstSort.sort((a, b) =>
      getPlatformTypeRank(
        getPlatformType(a.account),
        getPlatformType(b.account)
      )
    );
  }, [
    financialPlan.data,
    getPlatformType,
    getCardHeading,
    getCardHeadingRight,
  ]);

  const sortedAddedAccountOptions = useMemo(() => {
    const firstSort = orderBy(
      addedAccountOptions,
      [(el) => getCardHeading(el.value), (el) => getCardHeadingRight(el.value)],
      ["asc", "asc"]
    );

    return firstSort.sort((a, b) =>
      getPlatformTypeRank(a.platformType, b.platformType)
    );
  }, [addedAccountOptions, getCardHeading, getCardHeadingRight]);

  return (
    <>
      <PageHero
        className={styles.pageHero}
        innerClassName={styles.pageHeroInner}
        start={
          <TitleBlock
            title="Savings"
            subTitle="Add details about how much you plan to save."
          />
        }
        end={
          <CustomDropdownBase
            className={styles.dropdownBase}
            curItem={{
              value: "Add Savings",
              displayComponent: (
                <Text variant={"body"} color={"text"}>
                  Add Savings
                </Text>
              ),
            }}
            showDropdown={false}
            setShowDropdown={onOpenDrawer}
            width={240}
            isDisabled={false}
            hideIcon={true}
          />
        }
        endClassName={styles.pageHeroEnd}
      />

      <BodyContentFormatter>
        <div className={styles.takeUpSpace4} />

        <div className={styles.heading}>
          <Text variant={"heading1"} color={"text"}>
            Accounts
          </Text>

          <Text variant={"body"} color={"textSubtle"}>
            {pluralize(
              (financialPlan.data?.savings ?? []).length,
              "Account",
              true
            )}
          </Text>
        </div>

        <CardsGrid>
          {sortedSavings.map((s) => {
            const accountId = s.account as
              | FartherAccountId
              | AltAssetId
              | ExternalAccountId;

            return (
              <FilledCard
                key={s.savingsId}
                className={styles.filledCard}
                title={toUSD(parseFloat(s.contribution), {
                  minimumFractionDigits: 0,
                  maximumFractionDigits: 0,
                })}
                subTitle={"Contribution"}
                heading={getCardHeading(accountId)}
                headingSubtitle={getCardHeadingRight(accountId)}
                text={getNameOfClientOrSpouse(s.owner)}
                onClick={() => onClickCard(s.savingsId, undefined)}
              />
            );
          })}

          {sortedAddedAccountOptions
            .filter((a) =>
              (financialPlan.data?.savings ?? []).every(
                (s) => s.account !== a.value
              )
            )
            .map((el) => {
              return (
                <EmptyCard
                  key={el.value}
                  className={styles.emptyCard}
                  onAdd={() => onClickCard(undefined, el)}
                  heading={getCardHeading(el.value)}
                  text={getCardHeadingRight(el.value)}
                />
              );
            })}
        </CardsGrid>

        {initialPlanDateAndOwnerFields !== undefined && (
          <SavingsFormDrawer
            drawerOpen={showDrawer}
            onClose={onCloseDrawer}
            mutations={mutations}
            goalBasedFinancialPlanSavingsId={goalBasedFinancialPlanSavingsId}
            existingSavingsElement={existingSavingsElement}
            existingIncomeElements={financialPlan.data?.income ?? []}
            retirementHorizons={financialPlan.data?.retirementHorizon}
            initialPlanDateAndOwnerFields={initialPlanDateAndOwnerFields}
            addedAccountOptions={addedAccountOptions}
            defaultAccount={defaultAccount}
            hideSpouseOptions={!spouseInPlan}
            ownersOptions={ownersOptions}
            clientSpouseData={clientSpouseData}
          />
        )}
      </BodyContentFormatter>
    </>
  );
};

export default FinancialPlanSavingsAccounts;
